import { useEffect, useState } from "react";

import { useStudioSettings } from "../../stores/studio";
import { CANVAS_HEIGHT, CANVAS_WIDTH, LAYOUTS, layoutById } from "./layouts";
import { useStudioSession } from "./StudioSession";

/**
 * The browser studio, in a column of its own.
 *
 * ## Why it is in the console and not on a page of its own
 *
 * It was a page, briefly, and that was wrong. Hosting is not one task: it is
 * watching the queue, reading chat, reviewing what came in and skipping a track
 * that is not landing - all while on air. A studio on a separate page means
 * leaving the room to change a camera, which is the moment nobody is watching
 * the queue.
 *
 * ## And why it gets a whole column
 *
 * It briefly shared the deck's column, with its settings folded into a
 * disclosure to make room. That hid the half of the studio a host most wants to
 * play with - the layout, the background, which camera - behind a click, to fit
 * beside controls it has nothing to do with.
 *
 * The console now spends a column on the studio only when the studio is open,
 * and nothing on it when it is closed. So there is no shortage to design around
 * and every control is simply visible.
 *
 * ## Nothing here may unmount mid-broadcast
 *
 * The render loop, the devices and the peer connection all live in `useStudio`,
 * so this component disappearing takes the broadcast with it. That is why the
 * console opens it from a local toggle rather than from `room.source`: the room
 * is polled, and a single response that arrived while the status was rolling
 * over would otherwise drop a live show.
 */
export function StudioColumn() {
  const { deck, room, presets, credentials, missing } = useStudioSession();

  /*
   * Destructured, so everything below reads exactly as it did when these were
   * local `useState` values. Only the writes changed: they go through `set`,
   * because the studio's settings now outlive this component.
   */
  const {
    layoutId,
    showCard,
    showBrand,
    background,
    deckLevel,
    micLevel,
    camera,
    microphone,
    monitoring,
    set,
  } = useStudioSettings();

  /**
   * How many consecutive seconds the outgoing mix has been silent.
   *
   * Local, unlike the settings: it is a property of *looking* at the studio,
   * and resetting it when the host navigates away and back is correct - they
   * have just arrived and should be told again if it is still wrong.
   */
  const [silence, setSilence] = useState(0);

  /*
   * Silence, counted rather than assumed.
   *
   * A broadcast carrying no audio looks exactly like a working one from behind
   * the desk: the connection is up, the preview moves, the timer counts. The
   * only place the difference shows is a meter that never leaves zero, and a
   * host mid-set is not staring at a meter.
   *
   * Eight seconds before it says so, because the first few after starting are
   * legitimately quiet - the deck has to fetch a manifest and fill a buffer
   * before a single sample exists - and warning during that would train people
   * to ignore it.
   *
   * Not during a reconnect: the mix is genuinely silent to the server while
   * there is no connection, and saying "nothing is going out" over the top of
   * "the connection dropped" is two alarms for one problem.
   */
  useEffect(() => {
    if (deck.status !== "live") {
      setSilence(0);

      return;
    }

    const timer = setInterval(() => {
      setSilence((seconds) => (deck.level > 0.001 ? 0 : seconds + 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [deck.status, deck.level]);

  // The session fetches the room, so it can be absent for a moment on first
  // open. Nothing below reads well against a null room.
  if (!room) return <aside className="bg-app p-6" data-studio />;

  const ready = room.status === "live" && room.source === "stream";

  return (
    <aside
      className="flex min-w-0 flex-col gap-6 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
      data-studio
    >
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">Studio</span>
        <span
          className={`font-mono text-[11px] ${
            deck.status === "reconnecting" ? "text-hot-text" : "text-faint"
          }`}
          data-studio-connection
        >
          {deck.status === "reconnecting"
            ? "reconnecting…"
            : deck.status === "live"
              ? deck.connection
              : "off air"}
        </span>
      </div>

      {/*
        The preview and the encoder's canvas.

        The second is 720p and never displayed - `hidden` rather than
        unmounted, because `captureStream` needs a canvas that is in the
        document and an unmounted one produces a stream of nothing.
      */}
      {/*
        `shrink-0`, or there is no preview.

        This is a flex column that scrolls, and the column got much taller when
        the studio gained its own controls. Flex children shrink by default, and
        an `aspect-video` box has a min-content height of nothing - so the
        preview quietly collapsed to a 1px line while the canvas kept its
        1280x720 backing store and carried on compositing perfectly. The stream
        was fine; the host just could not see it.
      */}
      <div className="shrink-0 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black">
        <canvas
          ref={deck.previewRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="block aspect-video w-full"
          data-studio-preview
        />
      </div>

      {missing && (
        <p className="rounded-[var(--radius-control)] border border-line px-3 py-2.5 font-mono text-[11px] leading-relaxed text-ghost">
          the studio is not switched on for this platform yet
        </p>
      )}

      {deck.error && (
        <p
          role="alert"
          data-studio-error
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          {deck.error}
        </p>
      )}

      {(deck.status === "live" || deck.status === "reconnecting") && (
        <Meter level={deck.level} />
      )}

      {deck.status === "reconnecting" && (
        <p
          data-studio-reconnecting
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          the connection dropped — getting it back. your camera and mix are
          still running, so this picks up where it left off.
        </p>
      )}

      {silence >= 8 && (
        <p
          data-studio-silent
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          nothing is going out — the mix has been silent for {silence}s. check
          the deck is playing and the music level is up.
        </p>
      )}

      {/*
        No start button, and no go-live button.

        Both used to live here, which meant the host pressed "Go on air" in the
        header, found the room live but silent, and had to press a differently
        worded button in this column to actually broadcast. The header owns the
        action now and the session publishes as soon as the room is in studio
        mode - so this panel reports rather than asks.

        The exception is a failure: something has to offer another go, and it
        belongs next to the error that explains why.
      */}
      {deck.status === "error" && (
        <button
          type="button"
          disabled={!credentials}
          onClick={() => credentials && void deck.start(credentials.whip)}
          data-studio-retry
          className="rounded-[var(--radius-control)] bg-accent py-3 text-[13px] font-bold text-accent-ink transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Try again
        </button>
      )}

      {!ready && deck.status !== "error" && (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          {room.status === "live"
            ? "this room is on air from the catalogue — switch it to Studio above"
            : "set your camera and mix up here, then go on air"}
        </p>
      )}

      {/* ------------------------------------------------------------------ */}

      <Group label="Camera">
        <div className="flex flex-col gap-2.5">
          <Toggle
            on={deck.cameraOn}
            onClick={() =>
              deck.cameraOn
                ? deck.stopCamera()
                : void deck.startCamera(camera || undefined)
            }
            data-studio-camera
          >
            {deck.cameraOn ? "Camera on" : "Enable camera"}
          </Toggle>

          {/*
            Labels are empty until permission has been granted once, so before
            that this would be a list of blank entries. Offered only when there
            is genuinely a choice to make.
          */}
          {deck.devices.cameras.length > 1 && (
            <Picker
              label="Which camera"
              value={camera}
              options={deck.devices.cameras}
              onChange={(id) => {
                set("camera", id);
                // Switched live, because a host discovering they are on the
                // wrong camera wants it fixed now, not after a restart.
                if (deck.cameraOn) void deck.startCamera(id || undefined);
              }}
            />
          )}
        </div>
      </Group>

      <Group label="Microphone">
        <div className="flex flex-col gap-2.5">
          <Toggle
            on={deck.micOn}
            onClick={() =>
              deck.micOn
                ? deck.stopMic()
                : void deck.startMic(microphone || undefined)
            }
            data-studio-mic
          >
            {deck.micOn ? "Microphone on" : "Enable microphone"}
          </Toggle>

          {deck.devices.microphones.length > 1 && (
            <Picker
              label="Which microphone"
              value={microphone}
              options={deck.devices.microphones}
              onChange={(id) => {
                set("microphone", id);
                if (deck.micOn) void deck.startMic(id || undefined);
              }}
            />
          )}
        </div>
      </Group>

      {deck.passthrough && (
        <p
          data-studio-passthrough
          className="rounded-[var(--radius-control)] border border-fresh-edge bg-fresh-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-fresh"
        >
          passthrough — your camera goes out untouched, nothing drawn over it.
          use this with OBS's virtual camera. turn on an overlay to composite
          again.
        </p>
      )}

      <Group label="Layout">
        <div className="grid grid-cols-2 gap-2">
          {LAYOUTS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => set("layoutId", option.id)}
              aria-pressed={layoutId === option.id}
              className={`rounded-[var(--radius-control)] border px-3 py-2.5 text-left text-[12px] transition-colors ${
                layoutId === option.id
                  ? "border-accent bg-accent-wash text-ink"
                  : "border-line text-soft hover:border-line-lit hover:text-ink"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-ghost">
          {layoutById(layoutId).hint}
        </p>
      </Group>

      <Group label="On screen">
        <Check
          label="Now playing"
          on={showCard}
          onChange={(value) => set("showCard", value)}
        />
        <Check
          label="Your name"
          on={showBrand}
          onChange={(value) => set("showBrand", value)}
        />
      </Group>

      <Group label="Background">
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.preset}
              type="button"
              onClick={() => set("background", preset)}
              aria-pressed={background?.preset === preset.preset}
              aria-label={preset.label}
              title={preset.label}
              style={{
                background: `linear-gradient(${preset.angle}deg, ${preset.stops.join(", ")})`,
              }}
              className={`h-8 w-8 rounded-full border-2 transition-colors ${
                background?.preset === preset.preset
                  ? "border-ink"
                  : "border-transparent"
              }`}
            />
          ))}
        </div>
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-ghost">
          the same eight your profile header uses
        </p>
      </Group>

      <Group label="Mix">
        <Slider
          label="Music"
          value={deckLevel}
          onChange={(value) => set("deckLevel", value)}
        />
        <Slider
          label="Microphone"
          value={micLevel}
          onChange={(value) => set("micLevel", value)}
        />
        <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
          the balance of what goes out — what you hear does not change it
        </p>
      </Group>

      <Group label="Monitor">
        <Check
          label="Hear the deck"
          on={monitoring}
          onChange={(value) => set("monitoring", value)}
        />
        <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
          your speakers only — the music keeps going out either way, so you can
          talk over it without hearing yourself
        </p>
      </Group>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */

function Toggle({
  on,
  children,
  ...rest
}: {
  on: boolean;
  children: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={on}
      {...rest}
      className={`rounded-[var(--radius-control)] border py-2.5 text-[12px] font-bold transition-colors ${
        on
          ? "border-transparent bg-raised text-ink"
          : "border-line text-soft hover:border-line-strong hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Picker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: MediaDeviceInfo[];
  onChange: (id: string) => void;
}) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="w-full rounded-[var(--radius-control)] border border-line bg-app px-2.5 py-2 font-mono text-[11px] text-soft"
      >
        <option value="">System default</option>
        {options.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || "Unnamed device"}
          </option>
        ))}
      </select>
    </label>
  );
}

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="meta-caps mb-2.5 text-ghost">{label}</p>
      {children}
    </div>
  );
}

function Check({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1.5 text-[13px] text-ink">
      {label}
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[var(--color-accent)]"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="block py-1 text-[13px] text-ink">
      <span className="mb-1 flex items-center justify-between">
        {label}
        <span className="font-mono text-[10px] text-faint">
          {Math.round(value * 100)}
        </span>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--color-accent)]"
      />
    </label>
  );
}

/**
 * What is going out, right now.
 *
 * The connection state lives in the column header rather than here, because the
 * two answer the same question - "is anything reaching anybody?" - and a moving
 * meter on a failed connection is a host talking to nobody.
 */
function Meter({ level }: { level: number }) {
  const width = Math.min(100, Math.round(level * 140));

  return (
    <div className="flex items-center gap-3" data-studio-meter>
      <span className="meta-caps text-ghost">out</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-raised">
        <div
          className={`h-full transition-[width] duration-100 ${width > 92 ? "bg-hot" : "bg-fresh"}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}
